import { computed, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AppService } from '../../core/services/app.service';
import { RoomsStore } from '../../state/rooms.repository';
import { RoomPasswordStore } from '../../state/room-passwords.repository';

export type RoomState =
  | 'loading' // first rooms-snapshot not received yet — can't classify
  | 'known-with-password' // registry says hasPassword=true (or the server refused us), no stored password
  | 'known-with-stored-password' // registry (or the server) says it is a password room AND we hold the key
  | 'known-without-password' // registry says hasPassword=false
  | 'preset-from-link' // password in store, room not (yet) a registry password room
  | 'unknown'; // not in registry — treated as a brand-new room to be created

/**
 * Classifies the room (by server-minted `roomId`) the user is about to join or
 * create. Shared by the room-login and preparation-area screens (and the
 * e2e-encryption panel). An empty `roomId` (e.g. the create view) classifies as
 * `unknown`.
 *
 * For invite-link landings (`#p=...`) the password is already in
 * `RoomPasswordStore` (written by `AppService` at startup, keyed by roomId), so
 * this resolves to `preset-from-link` regardless of whether the room is in the
 * registry yet.
 *
 * Must be called in an injection context (field initializer / constructor).
 */
export function createRoomState(roomId: Signal<string>): Signal<RoomState> {
  const roomPasswords = inject(RoomPasswordStore);
  const appService = inject(AppService);
  const rooms = toSignal(inject(RoomsStore).rooms$, { initialValue: [] });

  return computed<RoomState>(() => {
    const id = roomId().trim();
    if (!id) return 'unknown';
    if (roomPasswords.has(id)) {
      // A registry-confirmed password room whose key we already hold is its
      // own state: joining can simply use the stored key, while creator-style
      // editing (regenerate / opt-out) would only desync it from the key other
      // members received via the invite link.
      const known = appService.firstSnapshotReceived()
        ? rooms().find((r) => r.roomId === id)
        : undefined;
      // The server confirming a password room counts like the registry does:
      // hidden rooms are never listed.
      return known?.hasPassword || roomPasswords.requiresPassword(id)
        ? 'known-with-stored-password'
        : 'preset-from-link';
    }
    // The server refused our join for a missing or wrong password: ask for it,
    // even for a hidden room the registry never lists.
    if (roomPasswords.rejection(id)) return 'known-with-password';
    if (!appService.firstSnapshotReceived()) return 'loading';
    const match = rooms().find((r) => r.roomId === id);
    if (!match) return 'unknown';
    return match.hasPassword ? 'known-with-password' : 'known-without-password';
  });
}
